import { useMutation, useQueryClient } from "@tanstack/react-query";

import { apiClient } from "../../api/apiClient";
import {
  reindexFileApiV1FilesIdReindexPost,
  removeFileApiV1FilesIdDelete,
  resetUserFolderApiV1FilesResetUserIdDelete,
  retrySummaryGenerationApiV1FilesIdRetrySummaryGenerationPost,
  updateClassificationApiV1FilesIdUpdateClassificationPut,
  updateDocumentReviewStatusEndpointApiV1FilesIdUpdateDocumentReviewStatusPut,
  updateFileMetadataEndpointApiV1FilesIdPatch,
  updateFullSummaryApiV1FilesIdUpdateFullSummaryPut,
  uploadFileApiV1FilesUploadPost,
} from "../../api/generated/sdk.gen";
import type {
  BodyUploadFileApiV1FilesUploadPost,
  DocSorterFileSchema,
  DocumentReviewStatusUpdateSchema,
  FileClassificationUpdateResponseSchema,
  FileMetadataUpdateSchema,
  FileUpdateSchema,
} from "../../api/generated/types.gen";
import { CLIENT_KEYS } from "../../constants/clientKeys";
import { buildApiErrorMessage } from "../../utils/notification";

/**
 * Hook for uploading a file
 */
export const useUploadFile = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: BodyUploadFileApiV1FilesUploadPost) => {
      const response = await uploadFileApiV1FilesUploadPost({
        body: data,
        client: apiClient,
      });

      if ("error" in response && response.error) {
        throw new Error(
          buildApiErrorMessage("Failed to upload file", response.error)
        );
      }

      return response.data;
    },
    onSuccess: () => {
      // Invalidate and refetch files query to refresh the list
      queryClient.invalidateQueries({
        queryKey: [CLIENT_KEYS.FILES],
      });
    },
    onError: (error) => {
      console.error("Error uploading file:", error);
    },
  });
};

/**
 * Hook for removing/deleting a file
 */
export const useRemoveFile = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (fileId: string) => {
      const response = await removeFileApiV1FilesIdDelete({
        path: { id: fileId },
        client: apiClient,
      });

      if ("error" in response && response.error) {
        throw new Error(
          buildApiErrorMessage("Failed to remove file", response.error)
        );
      }

      return response.data;
    },
    onSuccess: () => {
      // Invalidate and refetch files query to refresh the list
      queryClient.invalidateQueries({
        queryKey: [CLIENT_KEYS.FILES],
      });
    },
    onError: (error) => {
      console.error("Error removing file:", error);
    },
  });
};

/**
 * Re-trigger semantic-search indexing for several files at once. Backs the
 * "Re-index and answer" affordance shown when a case has files stuck at
 * indexing_status=FAILED, so an AI answer would otherwise miss their evidence.
 *
 * Fans out one reindex request per file and resolves when all have been
 * accepted; a single failure rejects the mutation so the caller can surface it.
 */
export const useReindexFiles = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (fileIds: string[]) => {
      await Promise.all(
        fileIds.map(async (fileId) => {
          const response = await reindexFileApiV1FilesIdReindexPost({
            path: { id: fileId },
            client: apiClient,
          });

          if ("error" in response && response.error) {
            throw new Error(
              buildApiErrorMessage(
                `Failed to re-index file ${fileId}`,
                response.error
              )
            );
          }
        })
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [CLIENT_KEYS.FILES] });
    },
    onError: (error) => {
      console.error("Error re-indexing files:", error);
    },
  });
};

/**
 * Hook for retrying summary generation for a file
 */
export const useRetrySummaryGeneration = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (fileId: string) => {
      const response =
        await retrySummaryGenerationApiV1FilesIdRetrySummaryGenerationPost({
          path: { id: fileId },
          client: apiClient,
        });

      if ("error" in response && response.error) {
        throw new Error(
          buildApiErrorMessage("Failed to retry summary generation", response.error)
        );
      }

      return response.data;
    },
    onSuccess: () => {
      // Invalidate and refetch files query to refresh the list
      queryClient.invalidateQueries({
        queryKey: [CLIENT_KEYS.FILES],
      });
    },
    onError: (error) => {
      console.error("Error retrying summary generation:", error);
    },
  });
};

/**
 * Hook for updating a file's full summary
 */
export const useUpdateFileSummary = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: { fileId: string; summary: FileUpdateSchema }) => {
      const { fileId, summary } = data;

      const response = await updateFullSummaryApiV1FilesIdUpdateFullSummaryPut({
        path: { id: fileId },
        body: summary,
        client: apiClient,
      });

      if ("error" in response && response.error) {
        throw new Error(
          buildApiErrorMessage("Failed to update file summary", response.error)
        );
      }

      return response.data;
    },
    onMutate: async () => {
      await queryClient.cancelQueries({ queryKey: [CLIENT_KEYS.FILES] });
    },
    onSuccess: (_data, { fileId, summary }) => {
      const withSummary = (file: DocSorterFileSchema) =>
        file.id === fileId ? { ...file, full_summary: summary.full_summary } : file;

      queryClient.setQueriesData<DocSorterFileSchema[] | DocSorterFileSchema>(
        { queryKey: [CLIENT_KEYS.FILES] },
        (oldData) => {
          if (!oldData) return oldData;
          return Array.isArray(oldData)
            ? oldData.map(withSummary)
            : withSummary(oldData);
        }
      );
    },
    onError: (error) => {
      console.error("Error updating file summary:", error);
    },
    // onMutate cancelled in-flight file fetches, so refetch even when the save fails.
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: [CLIENT_KEYS.FILES],
      });
    },
  });
};

/**
 * Hook for updating a file's document review status
 */
export const useUpdateDocumentReviewStatus = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: {
      fileId: string;
      reviewStatus: DocumentReviewStatusUpdateSchema;
    }) => {
      const { fileId, reviewStatus } = data;

      const response =
        await updateDocumentReviewStatusEndpointApiV1FilesIdUpdateDocumentReviewStatusPut(
          {
            path: { id: fileId },
            body: reviewStatus,
            client: apiClient,
          }
        );

      if ("error" in response && response.error) {
        throw new Error(
          buildApiErrorMessage(
            "Failed to update document review status",
            response.error
          )
        );
      }

      return response.data;
    },
    onSuccess: () => {
      // Invalidate and refetch files query to refresh the list
      queryClient.invalidateQueries({
        queryKey: [CLIENT_KEYS.FILES],
      });
    },
    onError: (error) => {
      console.error("Error updating document review status:", error);
    },
  });
};

/**
 * Hook for resetting a user's folder (removing all files for a user)
 */
export const useResetUserFolder = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (userId: string) => {
      const response = await resetUserFolderApiV1FilesResetUserIdDelete({
        path: { user_id: userId },
        client: apiClient,
      });

      if ("error" in response && response.error) {
        throw new Error(
          buildApiErrorMessage("Failed to reset user folder", response.error)
        );
      }

      return response.data;
    },
    onSuccess: () => {
      // Invalidate and refetch files query to refresh the list
      queryClient.invalidateQueries({
        queryKey: [CLIENT_KEYS.FILES],
      });
      // Also invalidate cases as this might affect them
      queryClient.invalidateQueries({
        queryKey: [CLIENT_KEYS.CASES],
      });
    },
    onError: (error) => {
      console.error("Error resetting user folder:", error);
    },
  });
};

/**
 * Hook to update a file's classification
 * @returns Mutation result for updating file classification with success/error handling
 */
export const useUpdateFileClassification = ({
  onSuccess,
  onError,
}: {
  // Using custom interface until the OpenAPI spec is updated to include response schema
  onSuccess?: (
    data: FileClassificationUpdateResponseSchema,
    fileId: string,
    classification: string
  ) => void;
  onError?: (error: Error) => void;
} = {}) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      fileId,
      classification,
    }: {
      fileId: string;
      classification: string;
    }) => {
      if (!fileId) {
        throw new Error("File ID is required");
      }

      if (!classification) {
        throw new Error("Classification is required");
      }

      const response = await updateClassificationApiV1FilesIdUpdateClassificationPut(
        {
          client: apiClient,
          path: {
            id: fileId,
          },
          body: {
            classification,
          },
        }
      );

      if ("error" in response && response.error) {
        throw new Error(
          buildApiErrorMessage(
            "Failed to update file classification",
            response.error
          )
        );
      }

      // Type assertion is needed until OpenAPI spec is updated to include response schema
      return response.data;
    },

    onMutate: async ({ fileId, classification }) => {
      await queryClient.cancelQueries({ queryKey: [CLIENT_KEYS.FILES] });

      // Snapshot the previous value
      const previousFiles = queryClient.getQueriesData({
        queryKey: [CLIENT_KEYS.FILES],
      });

      // Optimistically update all cached files queries
      queryClient.setQueriesData(
        { queryKey: [CLIENT_KEYS.FILES] },
        (oldData: DocSorterFileSchema[] | undefined) => {
          if (!oldData || !Array.isArray(oldData)) return oldData;

          return oldData.map((file: DocSorterFileSchema) => {
            if (file.id === fileId) {
              return { ...file, classification };
            }
            return file;
          });
        }
      );

      return { previousFiles };
    },

    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: [CLIENT_KEYS.FILES] });

      const responseData = data;
      const updatedFile = responseData.file;

      if (updatedFile?.id) {
        queryClient.setQueryData([CLIENT_KEYS.FILES, updatedFile.id], updatedFile);
      }

      onSuccess?.(data, variables.fileId, variables.classification);
    },

    onError: (error, _variables, context) => {
      // Revert the optimistic update on error
      if (context?.previousFiles) {
        context.previousFiles.forEach(([queryKey, data]) => {
          queryClient.setQueryData(queryKey, data);
        });
      }

      onError?.(error as Error);
    },
  });
};

/**
 * Hook for updating file metadata (date and author)
 */
export const useUpdateFileMetadata = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: {
      fileId: string;
      metadata: FileMetadataUpdateSchema;
    }) => {
      const { fileId, metadata } = data;

      const response = await updateFileMetadataEndpointApiV1FilesIdPatch({
        path: { id: fileId },
        body: metadata,
        client: apiClient,
      });

      if ("error" in response && response.error) {
        throw new Error(
          buildApiErrorMessage("Failed to update file metadata", response.error)
        );
      }

      return response.data;
    },
    onSuccess: () => {
      // Invalidate and refetch files query to refresh the list
      queryClient.invalidateQueries({
        queryKey: [CLIENT_KEYS.FILES],
      });
    },
    onError: (error) => {
      console.error("Error updating file metadata:", error);
    },
  });
};
